import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getAllUserMeetings } from "../../features/meetings/meetingsSlice";
import LoadingComponent from "../../components/common/LoadingComponent";
import { fetchIntegrationSettingsByUserId } from "../../features/integration-setting/integrationSettingSlice";
import { getIcon } from "../../constants/constants";
import { getStatusBadge } from "../Meetings/Meetings.utils";

const TaskProgress = ({ meeting = {} }) => {
  const total = meeting?.tasks?.total ?? 0;
  const completed = meeting?.tasks?.completed ?? 0;

  if (total === 0)
    return (
      <span className="text-body-quaternary fs--2 align-middle">No tasks</span>
    );

  const pct = Math.round((completed / total) * 100);

  return (
    <div className="d-flex align-items-center gap-2">
      <div className="progress" style={{ height: 4, width: 64 }}>
        <div className="progress-bar" style={{ width: `${pct}%` }} />
      </div>
      <span className="fs--2">
        {completed}/{total}
      </span>
    </div>
  );
};

const Meetings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { meetings, loading } = useSelector((state) => state.meetings);
  const { user } = useSelector((state) => state.user);

  useEffect(() => {
    if (user?.uuid) {
      dispatch(fetchIntegrationSettingsByUserId(user.uuid));
    }
  }, [user?.uuid, dispatch]);

  useEffect(() => {
    dispatch(getAllUserMeetings());
  }, [dispatch]);

  useEffect(() => {
    const meetingsList = Array.isArray(meetings)
      ? meetings
      : meetings?.meetings || [];

    const processingMeetings = meetingsList.filter(
      (meeting) => meeting.status === "PROCESSING",
    );

    if (processingMeetings.length === 0) return;

    const interval = setInterval(() => {
      dispatch(getAllUserMeetings());
    }, 10000);

    return () => clearInterval(interval);
  }, [meetings, dispatch]);

  const showNotFound = !loading && meetings.length === 0;
  const meetingCount = meetings.length;

  return (
    <div
      className="container py-5 mt-4 d-flex flex-column"
      style={{ maxWidth: "1400px", minHeight: "80vh" }}
    >
      <div className="text-center mb-5">
        <h2 className="fw-bold">Your Meetings Are Ready</h2>
        <p className="text-muted mb-0">
          View meetings imported from AI Notetakers, created manually, or
          provided as samples. You can now extract tasks automatically and
          perform actions across your connected platforms.
        </p>
      </div>

      {loading && <LoadingComponent loading />}

      {showNotFound && (
        <div className="d-flex justify-content-center mt-15 mb-15">
          <div className="bg-secondary rounded p-4 text-center shadow-sm border border-secondary">
            <p className="text-muted mb-0">
              Connect any AI Meeting Note Taker to import meetings here or later
              create meetings using transcript or your recordings
            </p>
          </div>
        </div>
      )}

      {!loading && !showNotFound && (
        <div className="d-flex flex-column gap-3">
          {meetings.map((meeting) => (
            <div
              key={meeting.uuid}
              className="card border shadow-none"
              style={{ borderRadius: 10 }}
            >
              <div className="card-body py-3 px-3">
                <div className="d-flex align-items-center justify-content-between gap-3">
                  <div className="d-flex align-items-center gap-3 flex-grow-1">
                    <img
                      src={
                        meeting.settingId?.integrationId?.name
                          ? getIcon(meeting.settingId.integrationId.name)
                          : "/logo192.png"
                      }
                      alt="logo"
                      style={{ width: 28, height: 28 }}
                    />

                    <div className="fw-semibold text-truncate">
                      {meeting.title || "Untitled Meeting"}
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-4 flex-shrink-0">
                    <div
                      className="d-none d-sm-block"
                      style={{
                        width: 1,
                        height: 16,
                        background: "var(--phoenix-border-color)",
                        flexShrink: 0,
                      }}
                    />
                      <span
                        className={`${getStatusBadge(meeting.status)} fs--2 d-flex align-items-center justify-content-center`}
                        style={{ minWidth: 90 }}
                      >
                        {meeting.status}
                      </span>
                    <div
                      className="d-none d-sm-block"
                      style={{
                        width: 1,
                        height: 16,
                        background: "var(--phoenix-border-color)",
                        flexShrink: 0,
                      }}
                    />
                    <div
                      className="d-flex align-items-center"
                      style={{ minWidth: 100 }}
                    >
                      <TaskProgress meeting={meeting} />
                    </div>
                    <div
                      className="d-none d-sm-block"
                      style={{
                        width: 1,
                        height: 16,
                        background: "var(--phoenix-border-color)",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      className="fs--2 text-body-secondary d-flex align-items-center justify-content-center"
                      style={{ minWidth: 110 }}
                    >
                      {new Date(meeting.createdAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="d-flex justify-content-between align-items-center mt-5 pt-4 border-top">
        <button className="btn btn-primary px-4" onClick={() => navigate("/onboarding/source")}>
          ← Previous
        </button>

        <div className="d-flex align-items-center gap-3">
          <button
          className="btn btn-primary px-4"
          disabled={meetingCount === 0}
          onClick={() => navigate("/onboarding/apps")}
        >
          Continue →
        </button>
        </div>
      </div>
    </div>
  );
};

export default Meetings;
